import { INode } from 'shared/types/tree'
import { INodeMapping } from '../tree/TreeActions'
import { NodeType } from 'shared/types/ingestion'

const CriticalPathTypes = [
  NodeType.Unit,
  NodeType.Document,
  NodeType.Assessment,
  NodeType.Activity,
  NodeType.LessonPlan,
  NodeType.Presentation,
]

export const PathSeparator = ':'

function closeEnoughStringMatch(st1: string, st2: string) {
  return st1 === st2 || st1.includes(st2) || st2?.includes(st1)
}

export interface IMatchParams {
  sourceTree: INode
  destTree: INode
  path: string
}

interface IPathComponent {
  type: string
  index: number
}

function stringToPath(path: string) {
  return path.split(PathSeparator).map((pathPart) => {
    const [type, indexPart] = pathPart.split('[')
    const index = parseInt(indexPart.split(']')[0])
    return { type, index }
  })
}

function getNodesForPathIndexes(
  tree: INode,
  [first, ...rest]: IPathComponent[],
) {
  if (first.type !== tree.type || first.index !== 0) {
    throw new Error('Invalid path start')
  }
  const nodeAtPath = (nodes: INode[], { index, type }: IPathComponent) => {
    const nextNode = nodes[index]
    if (nextNode?.type !== type) {
      return
    }
    return nextNode
  }

  return rest.reduce(
    (acc: (INode | undefined)[], pathComponent) => {
      const prevNode = acc[acc.length - 1]
      if (!prevNode) {
        return acc
      }
      acc.push(nodeAtPath(prevNode.nodes, pathComponent))
      return acc
    },
    [tree],
  )
}

function editDistance(s1: string, s2: string) {
  s1 = s1.toLowerCase()
  s2 = s2.toLowerCase()

  const costs = []
  for (let i = 0; i <= s1.length; i++) {
    let lastValue = i
    for (let j = 0; j <= s2.length; j++) {
      if (i === 0) costs[j] = j
      else {
        if (j > 0) {
          let newValue = costs[j - 1]
          if (s1.charAt(i - 1) !== s2.charAt(j - 1))
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1
          costs[j - 1] = lastValue
          lastValue = newValue
        }
      }
    }
    if (i > 0) costs[s2.length] = lastValue
  }
  return costs[s2.length]
}

export function bestTitleSort(title: string) {
  const titleParts = title?.split(':') || []
  return (n1: INode, n2: INode) => {
    if (titleParts.length) {
      const n1Parts = n1?.title.split(':') || []
      const n2Parts = n2?.title.split(':') || []
      if (n1Parts[0] !== n2Parts[0]) {
        if (titleParts[0] === n1Parts[0]) return -1
        if (titleParts[0] === n2Parts[0]) return 1
      }
    }

    const n1Match = editDistance(n1.title, title)
    const n2Match = editDistance(n2.title, title)
    return n1Match - n2Match
  }
}

export function findBestMatch({ path, destTree, sourceTree }: IMatchParams) {
  if (!path) return

  const [first, ...rest] = stringToPath(path)
  const sourceNodes = getNodesForPathIndexes(sourceTree, [first, ...rest])

  const goDownPath = (
    node: INode | undefined,
    pathParts: IPathComponent[],
    sourceNodes: (INode | undefined)[],
  ): INode | undefined => {
    if (!node) return
    if (!pathParts.length) {
      return node
    }

    const { type: nextType, index } = pathParts.shift()!

    const atIndex = node.nodes[index]
    const sourceTitle = sourceNodes.shift()?.title

    // I don't know if we should do this, the structure can really change
    // we can get messed up if we don't find the node and we don't look at neighbors
    // maybe we should always check neighbors if the main path fails?
    const nailedIt =
      atIndex &&
      atIndex.type === nextType &&
      [NodeType.LessonPlan, NodeType.Unit].includes(nextType as NodeType) &&
      atIndex.title &&
      sourceTitle &&
      closeEnoughStringMatch(atIndex.title, sourceTitle)

    let toCheck = [atIndex]
    if (!nailedIt) {
      let filter = (n: INode) => n.type === nextType
      if (
        nextType === NodeType.LessonPlan &&
        sourceTitle &&
        sourceTitle.split(':').length > 1
      ) {
        filter = (n) =>
          n.type === nextType &&
          n.title.split(':').shift() === sourceTitle.split(':').shift()
      }
      toCheck = node.nodes.filter(filter)
      sourceTitle && toCheck.sort(bestTitleSort(sourceTitle))
    }

    if (!pathParts.length) {
      return toCheck[0]
    }

    return toCheck.reduce((acc: INode[], child) => {
      const foundNode = goDownPath(child, [...pathParts], [...sourceNodes])
      if (foundNode) {
        acc.push(foundNode)
      }
      return acc
    }, [])[0]
  }
  if (first.type !== destTree.type || first.index !== 0) {
    throw new Error('Invalid path start')
  }
  const match = goDownPath(destTree, rest, sourceNodes.slice(1))

  return match
}

export interface INodeIdInfo {
  treePath: string
  node: INode
}

export type NodeIdMap = { [nodeId: string]: INodeIdInfo }

export function createIdMap(tree: INode): NodeIdMap {
  const nodeIdMap: NodeIdMap = {}
  const createMap = (node: INode, path: string[]) => {
    nodeIdMap[node.id] = {
      node,
      treePath: path.join(PathSeparator),
    }
    node.nodes?.forEach((n, i) => {
      createMap(n, [...path, `${n.type}[${i}]`])
    })
  }
  createMap(tree, [tree.type + '[0]'])
  return nodeIdMap
}

export interface IMappingUpdate {
  fromPath: string
  toPath: string
  oldToPath: string
  oldToTitle: string
  fromTitle: string
  toNode: INode
  mapping: INodeMapping
}

export function allMappingUpdates(
  missings: INodeMapping[],
  mismatched: INodeMapping[],
  sourceMaps: NodeIdMap,
  destMaps: NodeIdMap,
  destTree: INode,
  sourceTree: INode,
) {
  return [...missings, ...mismatched].reduce(
    (acc: IMappingUpdate[], missing) => {
      const { node: fromNode, treePath: fromPath } =
        sourceMaps[missing.from_node_id]
      const newToNode = findBestMatch({
        path: fromPath,
        sourceTree,
        destTree,
      })
      let oldToNode: INode | null = null
      if (missing.to_node_id) {
        oldToNode = destMaps[missing.to_node_id].node
        // Don't we found the same node it is already mapped to, don't include
        if (newToNode?.id === oldToNode.id) return acc
      }
      // Include update only when titles match
      if (newToNode?.title === fromNode.title) {
        const { treePath: toPath } = destMaps[newToNode.id]
        acc.push({
          mapping: missing,
          fromPath,
          toPath,
          oldToPath: oldToNode ? destMaps[oldToNode.id].treePath : '',
          oldToTitle: oldToNode ? destMaps[oldToNode.id].node.title : '',
          fromTitle: fromNode.title,
          toNode: newToNode,
        })
      }
      return acc
    },
    [],
  )
}

export function hasCriticalPathMismatch(path1: string, path2: string) {
  if (path1 === path2) return false
  const parts1 = path1?.split(PathSeparator) || []
  const parts2 = path2.split(PathSeparator)
  if (parts1.length !== parts2.length) return true
  return parts1.reduce((hasMismatch, part1Part, index) => {
    if (hasMismatch) return true
    const part2Part = parts2[index]
    if (part2Part === part1Part) return false
    const part2Type = part2Part.split('[')[0]
    const part1Type = part1Part.split('[')[0]
    // If types don't match we have a bad mapping
    if (part1Type !== part2Type) return true
    // If indexes don't match and we have a critical type we have a bad mapping
    return CriticalPathTypes.includes(part1Type as NodeType)
  }, false)
}

export enum MappingFilterType {
  UserGenerated = 'userGenerated',
  MismatchedPaths = 'mismatchedPaths',
  MismatchedTitles = 'mismatchedTitles',
  CriticalMismatchedPaths = 'criticalMismatchedPaths',
}

const filterForKey = (
  key: MappingFilterType,
  source: NodeIdMap,
  dest: NodeIdMap,
) => {
  switch (key) {
    case MappingFilterType.UserGenerated:
      return (mapping: INodeMapping) => mapping.user_modified
    case MappingFilterType.MismatchedPaths:
      return (m: INodeMapping) =>
        source[m.from_node_id].treePath !== dest[m.to_node_id!]?.treePath
    case MappingFilterType.MismatchedTitles:
      return (m: INodeMapping) => {
        if (m.deleted) return false
        return (
          source[m.from_node_id].node.title !== dest[m.to_node_id!]?.node.title
        )
      }
    case MappingFilterType.CriticalMismatchedPaths:
      return (m: INodeMapping) => {
        if (!m.to_node_id) return false
        return hasCriticalPathMismatch(
          source[m.from_node_id].treePath,
          dest[m.to_node_id].treePath,
        )
      }
    default:
      return () => true
  }
}

export function filteredMappings(
  mappings: INodeMapping[],
  sourceIdMapping: NodeIdMap,
  destIdMapping: NodeIdMap,
  ...filters: MappingFilterType[]
) {
  return filters.reduce((acc, filter) => {
    return acc.filter(filterForKey(filter, sourceIdMapping, destIdMapping))
  }, mappings)
}
